import * as React from "react";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router";
import { RioApiGet, RioApiPost, RioConfirmModal, RioExpandingPanel, RioIf, RioInput, RioLoadingIf, RioResources, RioValidationSummary, RioValidator} from "rio.react.all";
import { MergeFieldApiModel } from "../apiModels/MergeFieldApiModel";
import { Page } from "../components/Page";
import { ErrorModal } from "../modals/ErrorModal";
import { Editor } from "@tinymce/tinymce-react";
import { HttpErrorHelper } from "../helpers/HttpErrorHelper";
import { AccountStatementTemplateApiModel } from "../apiModels/AccountStatementTemplateApiModel";
import { AccountStatementApi } from "../apiLayer/AccountStatementApi";
import { MergeFieldApi } from "../apiLayer/MergeFieldApi";
import { AccountStatementEmailSendRequestApiModel } from "../apiModels/AccountStatementEmailSendRequestApiModel";
import { RouteHelper } from "../helpers/RouteHelper";


interface IAccountStatementsProps extends Partial<RouteComponentProps> {
}

interface IAccountStatementsState {
    loading: boolean;
    processing: boolean;
    accountStatementTemplateID: number;
    organisationID: number;
    organisationName: string;
    emailContent: string;
    subject: string;
    mergeFields: Array<MergeFieldApiModel>
    cancelClicked: boolean;

    changesMade: boolean;

    textFieldValue: string;

    editMode: boolean;
    responseError: string;
    sendProcessedSuccessfully: boolean;
}
export class AccountStatements extends React.Component<IAccountStatementsProps, IAccountStatementsState> {
    constructor(props: Readonly<IAccountStatementsProps>) {
        super(props);

        this.state = {
            loading: true,
            processing: false,
            accountStatementTemplateID: 0,
            organisationID: 0,
            organisationName: "",
            emailContent: "",
            subject: "",
            mergeFields: [],
            cancelClicked: false,
            changesMade: false,
            textFieldValue: "",

            editMode: false,
            responseError: null,
            sendProcessedSuccessfully: false
        };
    }

    private accountStatementTemplateID: number;
    private validator = new RioValidator();
    private editor: Editor;

    render() {
        return (
            <Page heading={RioResources.find("AccountStatements")}>
                <RioLoadingIf condition={!!this.state.loading || this.state.processing}>
                    <RioValidationSummary validator={this.validator} />

                    <RioExpandingPanel
                        resource={RioResources.find("AccountStatementTemplate")}
                        title={"- " + this.state.organisationName}
                        expandedDefault={true}
                        disableToggle={true}
                        actions={!!this.state.editMode ?
                            [
                                { resource: RioResources.find("Cancel"), onClick: () => this.handleCancelClicked(), disabled: this.state.processing, hasPermission: true},
                                { resource: RioResources.find("Save"), onClick: () => this.handleSaveClicked(), disabled: this.state.processing, hasPermission: true }
                            ]
                            :
                            [
                                { resource: RioResources.find("Edit"), onClick: () => this.setState({ editMode: true }), disabled: this.state.processing, hasPermission: true },
                                { resource: RioResources.find("Send"), onClick: () => this.handleSendClicked(), disabled: this.state.processing || this.state.sendProcessedSuccessfully, hasPermission: true }
                            ]}
                    >
                        <div>
                            <div className="form-row emailTitleBox">
                                <label className="emailTitleLabel">{RioResources.find("Subject").text}<span className="required">*</span></label>
                                <RioInput name="Title" type="text" className="emailTitleInput" value={this.state.subject} placeholder={RioResources.find("EmailTitlePlaceholder").text} disabled={!this.state.editMode} onChange={(e) => this.handleSubjectChanged(e.target.value)} validator={this.validator} validations={{ required: true, maxLength: 255 }} />
                            </div>
                        </div>

                        <Editor
							ref={(editor) => this.editor = editor}
							tinymceScriptSrc="../lib/tinymce/tinymce.min.js"
							//tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/5.10.5/tinymce.min.js"
                            initialValue={this.state.emailContent}
                            onChange={(e) => this.handleEditorChanged(e)}
                            disabled={!this.state.editMode}
                            init={{
                                plugins: 'link image code table mentions',
                                toolbar: 'mergefields | undo redo | bold italic underline strikethrough | fontsizeselect | forecolor forecolorpicker backcolor backcolorpicker | alignleft aligncenter alignright justify | sub sup | link unlink image | code',
                                fontsize_formats: '8px 10px 12px 14px 16px 18px 20px 22px 24px 26px 28px 30px 32px 34px 36px',
                                font_formats: 'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Segoe UI=Segoe UI, Lucida Grande, Tahoma, sans-serif; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats',
                                height: 600,
                                convert_urls: false,
                                setup: (editor: any) => {
                                    editor.ui.registry.addMenuButton('mergefields', {
                                        text: 'Merge Fields',
                                        fetch: (callback: any) => {
                                            var items = []
                                            for (var i = 0; i < this.state.mergeFields.length; i++) {
                                                var item = {
                                                    type: 'menuitem',
                                                    text: this.state.mergeFields[i].mergeFieldName,
                                                    value: this.state.mergeFields[i].mergeFieldSymbol,
                                                    onSetup: function () {
                                                        var $this = this;
                                                        this.onAction = function () {
                                                            editor.insertContent($this.data.value);
                                                        };
                                                    },
                                                }
                                                items.push(item)
                                            }
                                            callback(items);
                                        }
                                    });
                                },

                                images_upload_handler: (blobInfo: any, success: any, failure: any) => {
                                    let formData: FormData = new FormData();

                                    var xhr = new XMLHttpRequest();
                                    xhr.withCredentials = false;
                                    xhr.open('POST', '/api/Image/UploadImage');
                                    xhr.onload = function () {
                                        var json;

                                        if (xhr.status != 200) {
                                            failure('HTTP Error: ' + xhr.status);
                                            return;
                                        }
                                        json = JSON.parse(xhr.responseText);

                                        if (!json || typeof json.location != 'string') {
                                            failure('Invalid JSON: ' + xhr.responseText);
                                            return;
                                        }
                                        success(json.location);
                                    };
                                    formData.append('file', blobInfo.blob(), blobInfo.filename());
                                    xhr.send(formData);
                                }
                            }}

                        />

                    </RioExpandingPanel>

                    <RioIf condition={!!this.state.cancelClicked} then={
                        <RioConfirmModal text={RioResources.find("DiscardUnsavedChanges")} onConfirmModalClosed={(result) => this.handleConfirmCancel(result)} className="confirmModal" />
                    } />

                    <RioIf condition={!!this.state.responseError} then={
                        <ErrorModal text={this.state.responseError} onErrorModalClosed={() => this.setState({ responseError: null })} />
                    } />
                </RioLoadingIf>
            </Page>
        );
    }

    getSymbol() {
        return this.state.mergeFields[0].mergeFieldSymbol;
    }

    componentDidMount(): void {
        this.getTemplate();
        this.getMergeFields();
    }

    getTemplate() {
        AccountStatementApi.getCurrentTemplate()
            .then(response => {
                if (!!response) {
                    this.setState({
                        accountStatementTemplateID: response.accountStatementTemplateID,
                        organisationID: response.organisationID,
                        organisationName: response.organisationName,
                        subject: response.subject,
                        emailContent: response.emailContent,
                        textFieldValue: response.emailContent,
                        changesMade: false,
                    });
                }

                this.setState({
                    loading: false
                });
            })
            .catch((response) => {
                var error = HttpErrorHelper.handleHttpError(response, this, this.validator); //handles validator errors if validator passed
                this.setState({ responseError: error, loading: false });
            });
    }

    getMergeFields() {
        MergeFieldApi.getMergeFields()
            .then(response => {
                if (!!response) {
                    this.setState({
                        mergeFields: response
                    });
                }
            })
            .catch((response) => {
                var error = HttpErrorHelper.handleHttpError(response, this, this.validator); //handles validator errors if validator passed
                this.setState({ responseError: error });
            });
    }

    handleCancelClicked() {
        if (!!this.state.changesMade) {
            this.setState({
                cancelClicked: true
            });
        } else {
            this.handleConfirmCancel(true);
        }

    }

    handleConfirmCancel(result: boolean) {
        if (result) {
            window.location.reload();
        } else {
            this.setState({
                cancelClicked: false
            });
        }
    }

    handleSubjectChanged(value: string) {
        this.setState({
            subject: value,
            changesMade: true
        })
    }

    handleSaveClicked() {
        if (!!this.state.changesMade) {
            var editorContent = this.state.textFieldValue;
            //strip out all html, and trim it to see if any characters have been entered.
            const strippedEmail = editorContent.replace(/(<([^>]+)>)/ig, "").trim();

            if (strippedEmail.length === 0) {
                return;
            }

            let template = new AccountStatementTemplateApiModel();
            template.accountStatementTemplateID = !!this.state.accountStatementTemplateID ? this.state.accountStatementTemplateID : 0;
            template.organisationID = this.state.organisationID;
            template.emailContent = editorContent;
            template.subject = this.state.subject;

            AccountStatementApi.saveTemplate(template)
                .then((response) => {
                    window.location.reload();
                })
                .catch((response) => {
                    this.validator.addModelErrors(this, response.errors);
                    this.setState({
                        processing: false
                    });
                    //var error = HttpErrorHelper.handleHttpError(response, this, this.validator); //handles validator errors if validator passed
                    //this.setState({ responseError: error });
                });
            this.setState({
                emailContent: editorContent,
                processing: true
            });
        } else {
            this.handleConfirmCancel(true);
        }
    }

    handleEditorChanged(e: any) {
        let editorContent = e.target.getContent();
        if (editorContent != this.state.textFieldValue) {
            this.setState({
                textFieldValue: editorContent,
                changesMade: true
            });
        } 
    }

    handleSendClicked() {
        var editorContent = this.state.textFieldValue;
        //strip out all html, and trim it to see if any characters have been entered.
        const strippedEmail = editorContent.replace(/(<([^>]+)>)/ig, "").trim();

        if (strippedEmail.length === 0) {
            return;
        }

        window.location.href = "/SendAccountStatements";
    }
}
